import type { ReactNode } from "react";

export type FounderMetricTone = "blue" | "mint" | "coral" | "sand" | "steel" | "teal" | "violet" | "amber";

export type FounderMetric = {
  id: string;
  label: string;
  value: string | number;
  tone?: FounderMetricTone;
};

type FounderOperationalShellProps = {
  title?: string;
  showTitle?: boolean;
  metrics?: FounderMetric[];
  tabs?: Array<{ id: string; label: string }>;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  toolbar?: ReactNode;
  children: ReactNode;
};

export function FounderOperationalShell({
  title = "",
  showTitle = false,
  metrics = [],
  tabs,
  activeTab,
  onTabChange,
  toolbar,
  children,
}: FounderOperationalShellProps) {
  return (
    <section className="oac-founder-ops">
      {showTitle && title ? (
        <header className="oac-founder-ops__header">
          <h1 className="oac-founder-ops__title">{title}</h1>
          {toolbar ? <div className="oac-founder-ops__toolbar">{toolbar}</div> : null}
        </header>
      ) : toolbar ? (
        <header className="oac-founder-ops__header">
          <div className="oac-founder-ops__toolbar">{toolbar}</div>
        </header>
      ) : null}

      {metrics.length > 0 ? (
        <div className="oac-founder-ops__metrics">
          {metrics.map((metric) => (
            <article
              className={`oac-founder-ops-metric oac-founder-ops-metric--${metric.tone ?? "blue"}`}
              key={metric.id}
            >
              <p className="oac-founder-ops-metric__label">{metric.label}</p>
              <p className="oac-founder-ops-metric__value">{metric.value}</p>
            </article>
          ))}
        </div>
      ) : null}

      {tabs && tabs.length > 0 && onTabChange ? (
        <div className="oac-founder-ops__tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              aria-selected={activeTab === tab.id}
              className={
                activeTab === tab.id
                  ? "oac-founder-ops__tab oac-founder-ops__tab--active"
                  : "oac-founder-ops__tab"
              }
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="oac-founder-ops__body">{children}</div>
    </section>
  );
}

type FounderSplitPanelProps = {
  primary: ReactNode;
  secondary?: ReactNode;
  secondaryTitle?: string;
  onCloseSecondary?: () => void;
};

export function FounderSplitPanel({
  primary,
  secondary,
  secondaryTitle,
  onCloseSecondary,
}: FounderSplitPanelProps) {
  return (
    <div className={secondary ? "oac-founder-ops-split oac-founder-ops-split--open" : "oac-founder-ops-split"}>
      <div className="oac-founder-ops-split__primary">{primary}</div>
      {secondary ? (
        <aside aria-label={secondaryTitle ?? "Detail panel"} className="oac-founder-ops-split__secondary">
          <div className="oac-founder-ops-split__secondary-head">
            {secondaryTitle ? <h2>{secondaryTitle}</h2> : null}
            {onCloseSecondary ? (
              <button
                aria-label="Close detail panel"
                className="oac-founder-ops-split__close"
                onClick={onCloseSecondary}
                type="button"
              >
                ×
              </button>
            ) : null}
          </div>
          {secondary}
        </aside>
      ) : null}
    </div>
  );
}
